import api, {
  CreateMyTextPresetArgs,
  CreatePersonalFontArgs,
  CreateProjectTemplateRequest,
  CreateTextPresetResponse,
  CreateWaveformPreferenceResponse,
  DeleteFontArgs,
  DeleteMyTextPresetArgs,
  DeleteProjectTemplateRequest,
  DeleteWaveformPreferenceRequest,
  DisplayPreferencesResponse,
  GetProjectTemplateCompatibilityRequest,
  GetUserDisplayPreferencesArgs,
  GetUSTCompatibilityRequest,
  NotificationAlert,
  ProjectTemplateCompatibility,
  ProjectTemplateUpdateRequest,
  Questionnaire,
  QuestionnaireStatus,
  UpdatePersonalFontArgs,
  VideoExportUserPreferencesUpdateRequest,
} from '@sparemin/api-sdk';
import { normalize, schema } from 'normalizr';
import * as types from './types';

const personalFontSchema = new schema.Entity(
  'personalFonts',
  {},
  {
    idAttribute: 'familyName',
  },
);

const googleFontSchema = new schema.Entity(
  'googleFonts',
  {},
  {
    idAttribute: 'previewCssUrl',
  },
);

const sampleAudiosSchema = new schema.Entity(
  'sampleAudios',
  {},
  {
    idAttribute: 'id',
  },
);

const announcementsSchema = new schema.Entity(
  'announcements',
  {},
  {
    idAttribute: 'id',
  },
);

const projectTemplatesSchema = new schema.Entity(
  'projectTemplates',
  {},
  {
    idAttribute: 'id',
  },
);

const displayPrefsSchema = new schema.Entity(
  'displayPrefs',
  {},
  { idAttribute: 'userId' },
);

const waveformPrefsSchema = new schema.Entity('waveformPrefs', {});

const userPrefSchema = new schema.Entity(
  'userPrefs',
  {
    waveformPrefs: [waveformPrefsSchema],
  },
  { idAttribute: 'userId' },
);

const experimentsSchema = new schema.Entity(
  'experiments',
  {},
  {
    idAttribute: 'experimentName',
  },
);

const textPresetsSchema = new schema.Entity(
  'textPresets',
  {},
  { idAttribute: 'textPresetId' },
);

// NB: most of the data here is serialized using the user's id as a key in the object,
// however that is generally unnecessary as all of the user and display preferences
// are associated with the logged-in user.  we don't store any data for unauthenticated
// users.
//
// videoSizes in particular are not associated with the user, so the user id key
// is not used for this normalization.
const videoSizesSchema = new schema.Entity('videoSizes');

const eddySupportedProjectLanguagesSchema = new schema.Entity(
  'eddySupportedProjectLanguages',
  {},
  {
    idAttribute: eddySupportedLanguage => eddySupportedLanguage.languageCode,
  },
);

const automationSupportedLanguagesSchema = new schema.Entity(
  'automationSupportedLanguages',
  {},
  {
    idAttribute: automationSupportedLanguage =>
      automationSupportedLanguage.languageCode,
  },
);

const captionSupportedLanguagesSchema = new schema.Entity(
  'captionSupportedLanguages',
  {},
  {
    idAttribute: captionSupportedLanguage =>
      captionSupportedLanguage.languageCode,
  },
);

const notificationAlertSchema = new schema.Entity(
  'notificationAlert',
  {},
  { idAttribute: () => 'alerts' },
);

const widgetTemplateCompatibilitySchema = new schema.Entity(
  'widgetTemplateCompatibility',
  {},
  { idAttribute: 'widgetId' },
);

async function getMyPersonalFonts(): Promise<types.GetPersonalFontsResult> {
  const { personalFonts } = await api.getMyPersonalFonts();
  return normalize(personalFonts, [personalFontSchema]);
}

async function getGoogleFonts(): Promise<types.GetGoogleFontsResult> {
  const { items } = await api.getMyGoogleFonts();
  return normalize(items, [googleFontSchema]);
}

async function postPersonalFonts(args: CreatePersonalFontArgs): Promise<void> {
  return api.createPersonalFont(args);
}

async function updateFontName(args: UpdatePersonalFontArgs): Promise<void> {
  return api.updateFontName(args);
}

function deleteFont(args: DeleteFontArgs): Promise<void> {
  return api.deleteFont(args);
}

async function getMyDisplayPref(): Promise<DisplayPreferencesResponse> {
  return api.getMyDisplayPreferences();
}

async function updateMyCaptionsPrefs(args: types.PutCaptionsPrefArgs) {
  const { captionEnabled = false, captionLanguage = 'en-US' } = args;
  return api.updateMyCaptionPreferences({ captionEnabled, captionLanguage });
}

async function getUserDisplayPref(
  args: GetUserDisplayPreferencesArgs,
): Promise<types.GetUserDisplayPrefResult> {
  const { userId } = args;
  const result = await api.getUserDisplayPreferences(args);
  return normalize({ ...result, userId }, displayPrefsSchema);
}

async function getSampleAudios(): Promise<types.GetSampleAudiosResult> {
  const { sampleAudios } = await api.getSampleAudio();
  return normalize(sampleAudios, [sampleAudiosSchema]);
}

function getViewConfirmation(
  userId?: number,
  args?: types.GetViewConfirmationArgs,
): Promise<types.GetViewConfirmationResult> {
  return api.confirmAnnouncementViewed({ ...args, userId: userId as number });
}

async function getAnnouncements(): Promise<types.GetAnnouncementsResult> {
  const { announcements } = await api.getMyAnnouncements();
  return normalize(announcements, [announcementsSchema]);
}

function getOnboarding(
  args?: types.GetOnboardingArgs,
): Promise<types.GetOnboardingResult> {
  return api.getMyOnboardingItem(args) as Promise<types.GetOnboardingResult>;
}

function getOnboardingViewConfirmation(
  userId?: number,
  args?: types.GetOnboardingViewConfirmationArgs,
): Promise<types.GetOnboardingViewConfirmationResult> {
  return api.confirmOnboardingTutorialViewed({
    ...args,
    userId: userId as number,
  });
}

async function getProjectTemplates(): Promise<types.GetProjectTemplatesResult> {
  const { projectTemplates } = await api.getProjectTemplates();
  return normalize(projectTemplates, [projectTemplatesSchema]);
}

function createProjectTemplate(
  args?: CreateProjectTemplateRequest,
): Promise<types.CreateProjectTemplateResult> {
  return api.createProjectTemplate(args);
}

function overwriteProjectTemplate(
  args?: ProjectTemplateUpdateRequest,
): Promise<types.CreateProjectTemplateResult> {
  return api.updateProjectTemplate(args);
}

function deleteProjectTemplate(
  args?: DeleteProjectTemplateRequest,
): Promise<types.DeleteProjectTemplateResult> {
  return api.deleteProjectTemplate(args);
}

async function getProjectTemplateCompatibilityFromConfigurationId(
  args?: GetProjectTemplateCompatibilityRequest,
): Promise<types.GetProjectTemplateCompatibilityFromConfigurationIdResult> {
  const result = await api.getProjectTemplateCompatibility(args);
  return normalize(
    { ...result, widgetId: args.configurationId },
    widgetTemplateCompatibilitySchema,
  );
}

function getUserTemplateCompatibility(
  args?: GetUSTCompatibilityRequest,
): Promise<ProjectTemplateCompatibility> {
  return api.getProjectUSTCompatibility(args);
}

async function createQuestionnaireResponse(
  args: Questionnaire,
): Promise<types.CreateQuestionnaireResponseResult> {
  return api.createQuestionnaireResponse(args);
}

async function getQuestionnaireResponse(): Promise<Questionnaire> {
  return api.getMyQuestionnaireResponse();
}

async function getQuestionnaireStatus(): Promise<QuestionnaireStatus> {
  return api.getMyQuestionnaireStatus();
}

async function getMyUserPref(
  userId?: number,
): Promise<types.GetMyUserPrefResult> {
  const result = await api.getMyUserPreferences();
  return normalize({ ...result, userId }, userPrefSchema);
}

async function getPrefVideoSizes(): Promise<types.GetPrefVideoSizesResult> {
  const { data } = await api.getVideoExportSizes();
  return normalize(data, [videoSizesSchema]);
}

async function getEddySupportedProjectLanguages(): Promise<
  types.GetEddySupportedProjectLanguagesResult
> {
  const { data } = await api.getEddySupportedProjectLanguages();
  return normalize(data, [eddySupportedProjectLanguagesSchema]);
}

async function getAutomationSupportedLanguages(): Promise<
  types.GetAutomationSupportedLanguagesResult
> {
  const { data } = await api.getMakeAutomationSupportedLanguages();
  return normalize(
    data.map((language, originalPosition) => ({
      ...language,
      originalPosition,
    })),
    [automationSupportedLanguagesSchema],
  );
}

async function getCaptionSupportedLanguages(): Promise<
  types.GetCaptionSupportedLanguagesResult
> {
  const { data } = await api.getMakeCaptionSupportedLangauges();
  return normalize(
    data.map((language, originalPosition) => ({
      ...language,
      originalPosition,
    })),
    [captionSupportedLanguagesSchema],
  );
}

async function createWaveformPref(
  args: types.CreateWaveformPrefArgs,
): Promise<CreateWaveformPreferenceResponse> {
  return api.createWaveformPreference(args);
}

async function deleteWaveformPref(
  args: DeleteWaveformPreferenceRequest,
): Promise<types.DeleteWaveformPrefResult> {
  await api.deleteWaveformPreference(args);
  return args;
}

async function updateVideoExportPref(
  args: VideoExportUserPreferencesUpdateRequest,
): Promise<types.UpdateVideoExportPrefResult> {
  return api.updateVideoExportPreferences(args);
}

async function getExperimentVariant(
  args: types.GetExperimentVariantArgs,
): Promise<types.GetExperimentVariantResult> {
  const result = await api.getMyActiveExperimentVariants(args);
  return normalize(result, [experimentsSchema]);
}

async function updateColorPref(
  args: types.UpdateColorPrefArgs,
): Promise<types.UpdateColorPrefResult> {
  return api.updateColorPreferences({
    colorPrefs: args,
  });
}

async function getTextPresets(): Promise<types.GetTextPresetsResult> {
  const { textPresets } = await api.getMyTextPresets();
  return normalize(textPresets, [textPresetsSchema]);
}

async function createTextPreset(
  args: CreateMyTextPresetArgs,
): Promise<CreateTextPresetResponse> {
  return api.createMyTextPreset(args);
}

async function deleteTextPreset(args: DeleteMyTextPresetArgs): Promise<void> {
  return api.deleteMyTextPreset(args);
}

async function getNotificationAlert(): Promise<
  types.GetNotificationAlertResult
> {
  const result = await api.getMyNotificationAlert();

  return normalize(result, notificationAlertSchema);
}

async function updateNotificationAlert(
  args: NotificationAlert,
): Promise<types.UpdateNotificationAlertResult> {
  return api.updateNotificationAlert(args);
}

export const handle: types.IHandle = (
  method: any,
  args: any,
  _?: string,
  userId?: number,
): any => {
  switch (method) {
    case types.ServiceMethod.GET_MY_FONTS:
      return getMyPersonalFonts();

    case types.ServiceMethod.GET_GOOGLE_FONTS:
      return getGoogleFonts();

    case types.ServiceMethod.POST_PERSONAL_FONTS:
      return postPersonalFonts(args);

    case types.ServiceMethod.UPDATE_FONT_NAME:
      return updateFontName(args);

    case types.ServiceMethod.DELETE_FONT:
      return deleteFont(args);

    case types.ServiceMethod.GET_MY_DISPLAY_PREF:
      return getMyDisplayPref();

    case types.ServiceMethod.PUT_MY_CAPTIONS_PREF:
      return updateMyCaptionsPrefs(args);

    case types.ServiceMethod.GET_USER_DISPLAY_PREF:
      return getUserDisplayPref(args);

    case types.ServiceMethod.GET_SAMPLE_AUDIOS:
      return getSampleAudios();

    case types.ServiceMethod.GET_VIEW_CONFIRMATION:
      return getViewConfirmation(userId, args);

    case types.ServiceMethod.GET_ANNOUNCMENTS:
      return getAnnouncements();

    case types.ServiceMethod.GET_ONBOARDING:
      return getOnboarding(args);

    case types.ServiceMethod.GET_ONBOARDING_VIEW_CONFIRMATION:
      return getOnboardingViewConfirmation(userId, args);

    case types.ServiceMethod.GET_PROJECT_TEMPLATES:
      return getProjectTemplates();

    case types.ServiceMethod.CREATE_PROJECT_TEMPLATE:
      return createProjectTemplate(args);

    case types.ServiceMethod.OVERWRITE_PROJECT_TEMPLATE:
      return overwriteProjectTemplate(args);

    case types.ServiceMethod.DELETE_PROJECT_TEMPLATE:
      return deleteProjectTemplate(args);

    case types.ServiceMethod
      .GET_PROJECT_TEMPLATE_COMPATIBILITY_FROM_CONFIGURATION_ID:
      return getProjectTemplateCompatibilityFromConfigurationId(args);

    case types.ServiceMethod.GET_USER_TEMPLATE_COMPATIBILITY:
      return getUserTemplateCompatibility(args);

    case types.ServiceMethod.CREATE_QUESTIONNAIRE_RESPONSE:
      return createQuestionnaireResponse(args);

    case types.ServiceMethod.GET_QUESTIONNAIRE_RESPONSE:
      return getQuestionnaireResponse();

    case types.ServiceMethod.GET_QUESTIONNAIRE_STATUS:
      return getQuestionnaireStatus();

    case types.ServiceMethod.GET_MY_USER_PREF:
      return getMyUserPref(userId);

    case types.ServiceMethod.CREATE_WAVEFORM_PREF:
      return createWaveformPref(args);

    case types.ServiceMethod.DELETE_WAVEFORM_PREF:
      return deleteWaveformPref(args);

    case types.ServiceMethod.GET_EXPERIMENT_VARIANT:
      return getExperimentVariant(args);

    case types.ServiceMethod.UPDATE_COLOR_PREF:
      return updateColorPref(args);

    case types.ServiceMethod.UPDATE_VIDEO_EXPORT_PREF:
      return updateVideoExportPref(args);

    case types.ServiceMethod.GET_PREF_VIDEOS_SIZES:
      return getPrefVideoSizes();

    case types.ServiceMethod.GET_EDDY_SUPPORTED_PROJECT_LANGUAGES:
      return getEddySupportedProjectLanguages();

    case types.ServiceMethod.GET_TEXT_PRESETS:
      return getTextPresets();

    case types.ServiceMethod.CREATE_TEXT_PRESET:
      return createTextPreset(args);

    case types.ServiceMethod.DELETE_TEXT_PRESET:
      return deleteTextPreset(args);

    case types.ServiceMethod.GET_NOTIFICATION_ALERT:
      return getNotificationAlert();

    case types.ServiceMethod.UPDATE_NOTIFICATION_ALERT:
      return updateNotificationAlert(args);

    case types.ServiceMethod.GET_AUTOMATION_SUPPORTED_LANGUAGES:
      return getAutomationSupportedLanguages();

    case types.ServiceMethod.GET_CAPTION_SUPPORTED_LANGUAGES:
      return getCaptionSupportedLanguages();
  }

  return undefined;
};
